import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from "@mui/material";
import { checkInvoice, formatMoney, getPricing } from "./subscriptionPricing";
import { subscriptionCardSx } from "./subscriptionStyles";

export default function SubscriptionBillingReview({ subscription, error }) {
  const p = getPricing(subscription);
  const money = (value) => formatMoney(value, p.currency);
  const invoiceIssues = p.unpaid.filter(
    (log) => checkInvoice(log).matches === false
  ).length;
  const recordWarnings = [
    !p.historyComplete && {
      title: "Incomplete history",
      message: `Only ${subscription.change_logs?.length ?? 0} recent changes are available. Older unpaid charges may be missing.`,
    },
    p.missingCharges && {
      title: "Missing amounts",
      message:
        "Some unpaid charges have no valid amount after discount. The full balance cannot be confirmed.",
    },
    invoiceIssues > 0 && {
      title: "Discount differences",
      message: `${invoiceIssues} unpaid charge(s) differ from their recorded discount. Check the marked rows below.`,
    },
  ].filter(Boolean);
  return (
    <Paper
      component='section'
      aria-label='Billing breakdown'
      elevation={0}
      sx={{ ...subscriptionCardSx, p: { xs: 2, sm: 2.5 } }}
    >
      <Typography variant='h6' fontWeight={700}>
        Billing breakdown
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
        Current-plan projection, not a confirmed HitPay charge.
      </Typography>
      {(error || p.reconciled === false || recordWarnings.length > 0) && (
        <Stack
          spacing={1.5}
          sx={{
            mt: 2,
            "& .MuiAlert-root": { alignItems: "flex-start", borderRadius: 2 },
            "& .MuiAlert-message": {
              minWidth: 0,
              width: "100%",
              overflowWrap: "anywhere",
            },
            "& .MuiAlertTitle-root": {
              fontSize: "0.875rem",
              fontWeight: 700,
              mb: 0.5,
            },
          }}
        >
          {error && (
            <Alert severity='warning'>
              <AlertTitle>Refresh needed</AlertTitle>
              These are the last loaded figures. Refresh the subscription to see
              the latest amounts.
            </Alert>
          )}
          {p.reconciled === false && (
            <Alert severity='error'>
              <AlertTitle>Billing totals differ</AlertTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1,
                  mt: 1,
                }}
              >
                {[
                  ["Expected total", p.expectedTotal],
                  ["Recorded total", p.serverTotal],
                ].map(([label, amount]) => (
                  <Box key={label}>
                    <Typography variant='caption' display='block'>
                      {label}
                    </Typography>
                    <Typography
                      variant='body2'
                      fontWeight={700}
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {money(amount)}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography variant='caption' display='block' sx={{ mt: 1 }}>
                Expected total includes {money(p.expectedRecurring)} for the
                plan and {money(p.outstanding)} in unpaid charges.
              </Typography>
            </Alert>
          )}
          {recordWarnings.length > 0 && (
            <Alert severity='warning' variant='outlined'>
              <AlertTitle>Records to review</AlertTitle>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {recordWarnings.map((warning, index) => (
                  <Box
                    key={warning.title}
                    sx={{
                      pt: index ? 1.25 : 0,
                      borderTop: index ? 1 : 0,
                      borderColor: "divider",
                    }}
                  >
                    <Typography variant='body2' fontWeight={600}>
                      {warning.title}
                    </Typography>
                    <Typography variant='body2' sx={{ mt: 0.25 }}>
                      {warning.message}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      )}
      <Box
        sx={{
          my: 2,
          px: 2,
          py: 1,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.035),
        }}
      >
        {[
          [
            p.derivedRecurring == null
              ? "Recurring after discount (estimate)"
              : "Recurring after discount",
            p.recurring,
          ],
          ["Unpaid recorded charges", p.outstanding],
          ["Server billing projection", p.serverTotal],
        ].map(([label, amount], index) => (
          <Stack
            key={label}
            direction='row'
            justifyContent='space-between'
            alignItems='baseline'
            gap={2}
            sx={{
              py: 1,
              borderTop: index === 2 ? 1 : 0,
              borderColor: "divider",
            }}
          >
            <Typography
              variant='body2'
              color={index === 2 ? "text.primary" : "text.secondary"}
              fontWeight={index === 2 ? 700 : 400}
            >
              {label}
            </Typography>
            <Typography
              variant='body2'
              fontWeight={index === 2 ? 700 : 500}
              sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}
            >
              {money(amount)}
            </Typography>
          </Stack>
        ))}
      </Box>
      <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 0.5 }}>
        <Typography fontWeight={700}>Unpaid charges</Typography>
        <Chip size='small' label={`${p.unpaid.length} loaded`} />
      </Stack>
      <Typography variant='caption' color='text.secondary'>
        Each charge keeps its original discount. Today's discount is not
        reapplied.
      </Typography>
      {p.unpaid.length > 0 ? (
        <TableContainer sx={{ mt: 1 }}>
          <Table
            size='small'
            aria-label='Unpaid charge discount audit'
            sx={{ minWidth: 600, "& td": { whiteSpace: "nowrap" } }}
          >
            <TableHead>
              <TableRow>
                {[
                  "Change",
                  "Before discount",
                  "Recorded rate",
                  "Recorded charge",
                  "Arithmetic check",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {p.unpaid.map((log, index) => {
                const check = checkInvoice(log);
                return (
                  <TableRow key={log.scl_id || index}>
                    <TableCell>
                      {log.change_type}
                      <Typography
                        variant='caption'
                        display='block'
                        color='text.secondary'
                      >
                        {log.created_at
                          ? new Date(log.created_at).toLocaleDateString()
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{money(check.base)}</TableCell>
                    <TableCell>
                      {check.percent == null
                        ? "Not recorded"
                        : `${check.percent}%`}
                    </TableCell>
                    <TableCell>{money(check.net)}</TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        variant='outlined'
                        label={
                          check.matches == null
                            ? "Incomplete"
                            : check.matches
                              ? "Matches"
                              : `Expected ${money(check.expected)}`
                        }
                        color={
                          check.matches == null
                            ? "warning"
                            : check.matches
                              ? "success"
                              : "error"
                        }
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          {p.historyComplete
            ? "No unpaid charges recorded."
            : "No unpaid charges in the loaded history; older unpaid charges may exist."}
        </Typography>
      )}
    </Paper>
  );
}
